import { createSelector } from "@reduxjs/toolkit"
import { RootState } from "store/store"

import { Bike } from "models/bike"
import { isNotEmpty } from "utils"
import { BIKE_SOLD, BIKE_STOLEN } from "utils/constants"
import { ERROR, SUCCESS } from "../constant"

// Selector for the number of selected bikes.
export const numberOfBikeSelectedSelector = createSelector(
	(state: RootState) => state.bikeFleet.bikeFleet,
	(bikes: Array<Partial<Bike>>) => bikes.filter((bike) => bike.choice).length
	// Counts the number of bikes where 'choice' is true.
)

// Selector to return a string representation of the number of selected bikes.
export const numberOfBikeSelectedStringifySelector = createSelector(
	(state: RootState) => state.bikeFleet.bikeFleet,
	(bikes: Array<Partial<Bike>>) => {
		const numberOfBikeSelected = bikes.filter((bike) => bike.choice).length
		// Returns a string with the count, or an empty string if count is 0.
		return numberOfBikeSelected > 0 ? `(${numberOfBikeSelected})` : ""
	}
)

// Selector for the selected bikes' IDs and row versions.
export const fnuciBikeSelectedSelector = createSelector(
	(state: RootState) => state.bikeFleet.bikeFleet,
	(bikes: Array<Partial<Bike>>) => {
		// Filters the selected bikes and maps to an object with 'bike_id' and 'row_version'.
		return bikes
			.filter((bike) => bike.choice)
			.map((bike) => ({ bike_id: bike.bike_id, row_version: bike.row_version }))
	}
)

// Selector for getting detailed information of selected bikes for export.
export const bikeFleetForExportSelectedSelector = createSelector(
	(state: RootState) => state.bikeFleet.bikeFleet,
	(bikes: Array<Partial<Bike>>) =>
		bikes
			.filter((bike) => bike.choice)
			.map((bike) => ({
				// Maps each selected bike to an object containing detailed information.
				fnuciCode: bike.bike_id,
				operator: bike.operator,
				brand: bike.bike_description.brand,
				model: bike.bike_description.model,
				type: bike.bike_description.gear_type,
				color: bike.bike_description.color,
				status: bike.bike_status,
				activation: bike.bike_last_registered_by.create_date,
			}))
)

// Selector to check if no status type is selected for the bike fleet.
export const noStatusSelectedSelector = createSelector(
	(state: RootState) => state.bikeFleet.currentBikeFleetStatusSelected,
	(selectedStatus) => !selectedStatus
	// Returns true if no status type is selected.
)

// Selector to check if the 'Stolen' status is selected for the bike fleet.
export const isStolenStatusSelectedBikeFleetSelector = createSelector(
	(state: RootState) => state.bikeFleet.currentBikeFleetStatusSelected,
	(selectedStatus) => selectedStatus && Number(selectedStatus) === BIKE_STOLEN
	// Compares the selected status with the BIKE_STOLEN constant.
)

// Selector to check if the 'Sold' status is selected for the bike fleet.
export const isInSoldStatusSelectedBikeFleetSelector = createSelector(
	(state: RootState) => state.bikeFleet.currentBikeFleetStatusSelected,
	(selectedStatus) => selectedStatus && Number(selectedStatus) === BIKE_SOLD
	// Compares the selected status with the BIKE_SOLD constant.
)

// Selector to compare a given status with the selected status for the bike fleet.
export const statusSelectedSelector = createSelector(
	(state: RootState, params: number) => state.bikeFleet.currentBikeFleetStatusSelected,
	(params) => params,
	(selectedStatus, params) => selectedStatus === params
	// Returns true if the given status (params) matches the selected status.
)

// Selector for Message box when user updated bike
export const statusMessageSelector = createSelector(
	(state: RootState) => state.bikeFleet.updateBike.error,
	(state: RootState) => state.bikeFleet.updateBike.success,
	(errorBike, successBike) =>
		isNotEmpty(errorBike)
			? { message: "errorChangeStatus", type: ERROR }
			: isNotEmpty(successBike)
			? { message: "successChangeStatus", type: SUCCESS }
			: false
)

export const hasBikeIntoBikeFleetSelector = createSelector(
	(state: RootState) => state.bikeFleet.bikeFleet,
	(bikeFleet) => {
		return bikeFleet?.length > 0
	}
)
